import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";

const KEY = "jx_install_dismissed_at";
const SNOOZE_MS = 7 * 24 * 3600 * 1000;
let deferred = null;
const listeners = new Set();

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e; listeners.forEach(f => f()); });
  window.addEventListener("appinstalled", () => { deferred = null; listeners.forEach(f => f()); });
}

const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

export const canInstall = () => !!deferred;
export async function promptInstall() {
  if (!deferred) return false;
  deferred.prompt();
  const { outcome } = await deferred.userChoice;
  deferred = null; listeners.forEach(f => f());
  return outcome === "accepted";
}
export function useInstallable() {
  const [, force] = useState(0);
  useEffect(() => { const f = () => force(n => n + 1); listeners.add(f); return () => listeners.delete(f); }, []);
  return { available: !!deferred, ios: isIOS() && !isStandalone(), installed: isStandalone() };
}

export const InstallBanner = () => {
  const { available, ios, installed } = useInstallable();
  const [hidden, setHidden] = useState(() => Date.now() - Number(localStorage.getItem(KEY) || 0) < SNOOZE_MS);
  if (installed || hidden || (!available && !ios)) return null;
  const close = () => { localStorage.setItem(KEY, String(Date.now())); setHidden(true); };

  return (
    <div data-testid="install-banner" className="fixed z-50 left-3 right-3 top-[4.5rem] lg:top-auto lg:bottom-6 lg:left-6 lg:right-auto lg:w-96 rounded-2xl glass border border-[#FF3EA5]/40 p-3 shadow-[0_0_30px_rgba(255,62,165,0.25)] flex items-center gap-3 animate-in fade-in slide-in-from-top-2">
      <img src="/icon-192.png" alt="Jixxeo" className="w-11 h-11 rounded-xl shrink-0"/>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm">Install Jixxeo Videos</div>
        {ios ? (
          <p className="text-[11px] text-white/60" data-testid="install-ios-hint">Tap <Share size={11} className="inline -mt-0.5"/> Share, then "Add to Home Screen"</p>
        ) : (
          <p className="text-[11px] text-white/60">Full-screen app, offline downloads & alerts</p>
        )}
      </div>
      {!ios && (
        <button onClick={async () => { if (await promptInstall()) close(); }} data-testid="install-banner-btn"
          className="jx-btn h-9 px-4 rounded-full bg-[#00E7FF] text-black text-xs font-extrabold inline-flex items-center gap-1 shrink-0"><Download size={13}/> Install</button>
      )}
      <button onClick={close} data-testid="install-banner-close" className="p-1.5 rounded-full hover:bg-white/10 shrink-0"><X size={14}/></button>
    </div>
  );
};
